import {RouteComponentProps, useHistory, withRouter} from "react-router-dom";
import {useSuspenseAnimal} from "../../Common/hooks/use-animal";
import {Document, Link, Page, PDFDownloadLink, PDFViewer, StyleSheet, Text, View} from "@react-pdf/renderer";
import React, {PropsWithChildren} from "react";
import {useTranslation} from "react-i18next";
import {JsonAnimal} from "../../../api/generated/rest-dto";
import {ArrowBack, Downloading, FileDownload} from "@mui/icons-material";
import {PedigreeService} from "../../../services/pedigree-service";
import {PedigreeDto} from "../../../api/dtos/pedigree-dto";
import {Button, Stack} from "@mui/material";
import RouteService from "../../../services/route-service";
import {useSuspenseQuery} from "@tanstack/react-query";
import {TreatmentListJson} from "../../../api/generated/medical-rest";
import TreatmentService from "../../../services/treatment-service";
import {primaryTextFormatter} from "../TreatmentList/treatment-list-item";
import {DateTime} from "luxon";

const styles = StyleSheet.create({
    page: {
        padding: '2cm',
        fontSize: 14,
        fontFamily: 'Times-Roman',
    },
    title: {
        fontSize: 22,
        fontWeight: 'bold',
        textAlign: 'center',
        marginTop: '6mm',
        marginBottom: '4mm',
    },
    heading: {
        fontSize: 18,
        fontWeight: 'bold',
        textAlign: 'center',
        marginTop: '6mm',
        marginBottom: '4mm',
    },
    footer: {
        fontSize: 12,
        position: 'absolute',
        bottom: 0,
        left: 0,
        right: 0,
        padding: '0 2cm 1cm',
    },
    dimmed: {
        color: '#666',
    }
});

const PdfTitle = ({children}: PropsWithChildren<{}>) => (
    <Text style={styles.title}>{children}</Text>
);

const PdfHeading = ({children}: PropsWithChildren<{}>) => (
    <Text style={styles.heading}>{children}</Text>
);

const Definition = ({title, children}: PropsWithChildren<{ title: string }>) => (
    <View style={{flexDirection: 'row', gap: 4}}>
        <Text style={{minWidth: '20vw'}}>{title}</Text>
        <Text>{children}</Text>
    </View>
)

type AnimalDocumentProps = {
    animal: JsonAnimal,
    pedigree: PedigreeDto | undefined,
    treatments: TreatmentListJson[],
};

const AnimalDocument = ({animal, pedigree, treatments}: AnimalDocumentProps) => {
    const {t} = useTranslation();

    const dam = pedigree?.root.dam;
    const sire = pedigree?.root.sire;

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                <PdfTitle>{animal.herdCode} {animal.name}</PdfTitle>

                <PdfHeading>&mdash; Stammdaten &mdash;</PdfHeading>

                <View>
                    <Definition title="Herdkürzel:">{animal.herdCode}</Definition>
                    <Definition title="Name:">{animal.name}</Definition>
                    <Definition title="Geschlecht:">{t('sex.' + animal.sex)}</Definition>
                    <Definition title="Farbe:">{t('color.' + animal.fiberColor)}</Definition>
                    <Definition title="Zusatzfarben:">{animal.secondaryColors.map(c => t('color.' + c)).join(', ')}</Definition>
                    <Definition title="Augenfarbe:">{animal.eyeColor ? t('eyecolor.' + animal.eyeColor) : '---'}</Definition>
                    <Definition title="Geburtsdatum:">{animal.dateOfBirth?.toLocaleString() ?? 'N/A'}</Definition>
                    <Definition title="Geburtsland:">{animal.countryOfBirth ?? 'N/A'}</Definition>
                    <Definition title="Rasse:">{t('breed.' + animal.breed)}</Definition>
                </View>

                <PdfHeading>&mdash; Identifikationsnummern &mdash;</PdfHeading>

                <View>
                    {animal.panonIdentifier.animalIdentifiers.length
                        ? animal.panonIdentifier.animalIdentifiers.map(ai => (
                            <Definition key={ai.type + ai.position + ai.value}
                                        title={`${t('identifier.type.' + ai.type)} ${t(ai.position)}`}>
                                {ai.value}
                            </Definition>
                        ))
                        : <Text>N/A</Text>}
                </View>

                {!!pedigree && <>
                    <PdfHeading>&mdash; Stammbaum &mdash;</PdfHeading>

                    <View>
                        <Definition title="Vater:">{sire ? sire.fullName : 'unbekannt'}</Definition>
                        <Definition title="Mutter:">{dam ? dam.fullName : 'unbekannt'}</Definition>
                    </View>
                </>}

                <PdfHeading>&mdash; Behandlungen &mdash;</PdfHeading>

                {treatments
                    .filter(treatment => !treatment.private)
                    .sort((t1, t2) => t1.timestamp.toMillis() - t2.timestamp.toMillis())
                    .map(treatment => (
                    <View key={treatment.id} style={{marginBottom: '8px'}}>
                        <Definition title={treatment.timestamp.toLocaleString(DateTime.DATE_SHORT)}>{primaryTextFormatter.format(treatment)}</Definition>
                        <Text style={styles.dimmed}>{treatment.note}</Text>
                    </View>
                ))}

                <View fixed style={styles.footer}>
                    <View style={{flexDirection: 'row'}}>
                        <Text style={{marginRight: 'auto'}}>
                            Quelle: <Link src="https://herds.alpacuna.com/">https://herds.alpacuna.com</Link>
                        </Text>

                        <Text style={{textAlign: 'left'}}
                              render={({pageNumber, totalPages}) => `Seite ${pageNumber} / ${totalPages}`}/>
                    </View>
                </View>
            </Page>
        </Document>
    );
};

const AnimalPdf = ({match}: RouteComponentProps<{ panonId: string }>) => {
    const {panonId} = match.params;
    const {animal} = useSuspenseAnimal(panonId);
    const history = useHistory();

    const {data: pedigree} = useSuspenseQuery({
        queryKey: ['pedigree', animal?.id],
        queryFn: () => animal ? PedigreeService.loadPedigree(animal) : null,
    });

    const {data: treatments} = useSuspenseQuery({
        queryKey: ['treatments', animal?.id],
        queryFn: async () => animal ? TreatmentService.loadTreatmentsForAnimalId(animal.id) : null,
    });

    if (!animal || !pedigree || !treatments) {
        return null;
    }

    const handleBackButton = () => history.push({
        pathname: RouteService.expand(RouteService.ANIMAL_DETAILS, {panonId: animal.panonIdentifier.id})
    });

    const document = <AnimalDocument animal={animal} pedigree={pedigree} treatments={treatments}/>;

    return (
        <Stack direction="column" spacing={2} margin={2}>
            <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<ArrowBack/>} onClick={handleBackButton}>
                    Zurück
                </Button>
                <PDFDownloadLink document={document}
                                 fileName={`${animal.herdCode}_${animal.name}.pdf`}>
                    {({loading}) => (
                        <Button startIcon={loading ? <Downloading/> : <FileDownload/>} variant="contained" disabled={loading}>
                            {loading ? 'PDF wird generiert' : 'Download PDF'}
                        </Button>
                    )}
                </PDFDownloadLink>
            </Stack>
            <PDFViewer width="100%" height="640">
                {document}
            </PDFViewer>
        </Stack>
    );
};

export default withRouter(AnimalPdf);
